import { z } from "zod";

import type { ReviewPlatform } from "../enums/review-platform";

/**
 * The Reviews block — the agency's synced Google, Tripadvisor, Trustpilot and
 * Facebook reviews, one tab per platform (design.md D98).
 *
 * The block is a stored Tabs block with one tab and one panel per platform
 * switched on. Each panel stores two data nodes, each naming its `platform`
 * and `limit`: a summary (the score) and, inside a stored Carousel, a row (one
 * slide per review). Both ask the same question, so they share one read
 * (`reviewsQueryKey`). A panel whose row comes back empty is dropped with its
 * tab when the page is bound (`review-tabs.ts`).
 */

/** The question both data nodes ask, and the type traveler-api answers it
 *  under. No stored node carries this type. */
export const REVIEWS_TYPE = "reviews";

/** The list inside a panel's Carousel: one slide per review. */
export const REVIEW_ROW_TYPE = "review-row";

/** A panel's score line: the platform's score, total and listing link. */
export const REVIEW_SUMMARY_TYPE = "review-summary";

/** The Platform reviews block's list: one platform's reviews as a grid of
 *  cards, with no Carousel (design.md D101). */
export const REVIEW_GRID_TYPE = "review-grid";

/**
 * The tab order. Fixed, not the order switched on: the design draws Google
 * first and Facebook last whatever the operator did.
 */
export const REVIEWS_BLOCK_PLATFORMS = [
  "google",
  "tripadvisor",
  "trustpilot",
  "facebook",
] as const satisfies readonly ReviewPlatform[];

export const DEFAULT_REVIEWS_LIMIT = 9;

/**
 * Reviews per platform. Up to four platforms ride the page's cached payload,
 * each with a full review body, so the cap is a payload budget.
 */
export const MIN_REVIEWS_LIMIT = 3;
export const MAX_REVIEWS_LIMIT = 12;

/**
 * A platform switch. Unset reads as ON, the design's default; the one
 * spelling a round-trip produces for "off" is `false`.
 */
export const readReviewsPlatformSwitch = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  return String(value).toLowerCase() !== "false";
};

/**
 * The platforms a block asks for, in tab order. The block needs a source, so
 * every switch off reads as Google alone. The editor never lets the last
 * platform's tab go (`reviews-tabs.ts` in the page builder).
 */
export const readReviewsPlatforms = (
  attributes: Record<string, unknown>,
): ReviewPlatform[] => {
  const on = REVIEWS_BLOCK_PLATFORMS.filter((platform) =>
    readReviewsPlatformSwitch(attributes[platform]),
  );
  return on.length > 0 ? on : ["google"];
};

const platformSwitch = z
  .unknown()
  .optional()
  .transform(readReviewsPlatformSwitch);

/**
 * Traits are strings, so `limit` arrives as `"9"` and is coerced here, and
 * each switch arrives as `"true"` / `"false"`. A plain object, field by
 * field, as `blogPostsQuerySchema` is: operator-api documents a query from
 * its object shape (`build-openapi-document.ts`), and a schema wrapped in a
 * transform would have none. `reviewsQueryPlatforms` reads the switches.
 */
export const reviewsQuerySchema = z.object({
  google: platformSwitch,
  tripadvisor: platformSwitch,
  trustpilot: platformSwitch,
  facebook: platformSwitch,
  limit: z.coerce
    .number()
    .int()
    .min(MIN_REVIEWS_LIMIT)
    .max(MAX_REVIEWS_LIMIT)
    .catch(DEFAULT_REVIEWS_LIMIT)
    .default(DEFAULT_REVIEWS_LIMIT),
});

export type ReviewsQuery = z.infer<typeof reviewsQuerySchema>;

/** The platforms a query asks for, in tab order; never none. */
export const reviewsQueryPlatforms = (query: ReviewsQuery): ReviewPlatform[] =>
  readReviewsPlatforms(query);

/** Two blocks asking for the same platforms and count are one read. */
export const reviewsQueryKey = (query: ReviewsQuery): string =>
  [
    REVIEWS_TYPE,
    reviewsQueryPlatforms(query).join(","),
    String(query.limit),
  ].join("|");

/** A row's or summary's stored platform; anything unknown reads as Google. */
export const readReviewPlatform = (value: unknown): ReviewPlatform =>
  REVIEWS_BLOCK_PLATFORMS.find((platform) => platform === value) ?? "google";

/** The query a row or summary asks: its own platform alone, and its limit. */
export const reviewsQueryForPlatform = (
  attributes: Record<string, unknown>,
): ReviewsQuery => {
  const platform = readReviewPlatform(attributes.platform);
  return reviewsQuerySchema.parse({
    ...Object.fromEntries(
      REVIEWS_BLOCK_PLATFORMS.map((each) => [each, String(each === platform)]),
    ),
    limit: attributes.limit,
  });
};

/**
 * One review, with its platform's summary on it.
 *
 * The summary repeats on every review of a platform because the content
 * binding answers a block with ONE flat list (`ContentItem[]`), and a stored
 * card (D97) is drawn once per item. The renderer groups the list by
 * `platform` and reads the summary off the first review of each group.
 *
 * `platformRating` and `platformReviewTotal` are what the platform reports for
 * the whole listing ("4.9 · 312 reviews"). When the provider sent none, they
 * fall back to the average and count of the reviews we keep.
 */
export const reviewCardItemSchema = z.object({
  id: z.string(),
  platform: z.enum(REVIEWS_BLOCK_PLATFORMS),
  reviewerName: z.string(),
  rating: z.number().int(),
  title: z.string().nullable(),
  body: z.string(),
  images: z.array(z.string()),
  /** ISO timestamp. */
  reviewedAt: z.string(),
  /** Facebook's "recommends", when the platform sent it. */
  recommended: z.boolean().nullable(),
  platformRating: z.number(),
  platformReviewTotal: z.number().int(),
  /** The listing's public page, for "See all on …". Null when unknown. */
  platformUrl: z.string().nullable(),
});

export type ReviewCardItem = z.infer<typeof reviewCardItemSchema>;

export const REVIEW_PLATFORM_NAMES: Record<ReviewPlatform, string> = {
  google: "Google",
  tripadvisor: "Tripadvisor",
  trustpilot: "Trustpilot",
  facebook: "Facebook",
};

/**
 * Tripadvisor's own country sites. A list, not a pattern: any open-ended
 * ending also passes `tripadvisor.evil.com`. A listing on a site missing here
 * gets no "See all" link, which is the safe way to be wrong.
 */
const TRIPADVISOR_ENDINGS = [
  "com",
  "co.uk",
  "ca",
  "com.au",
  "co.nz",
  "ie",
  "in",
  "com.sg",
  "com.my",
  "com.ph",
  "co.id",
  "co.za",
  "de",
  "at",
  "ch",
  "fr",
  "be",
  "it",
  "es",
  "pt",
  "nl",
  "se",
  "dk",
  "no",
  "fi",
  "pl",
  "cz",
  "sk",
  "co.hu",
  "rs",
  "ru",
  "com.tr",
  "com.gr",
  "co.il",
  "com.eg",
  "jp",
  "co.kr",
  "com.hk",
  "com.tw",
  "cn",
  "com.vn",
  "com.br",
  "com.mx",
  "com.ar",
  "cl",
  "co",
  "com.pe",
  "com.ve",
] as const;

const escapeDots = (ending: string): string => ending.replaceAll(".", "\\.");

/**
 * The hosts a stored listing URL may point at. The identifier is typed by the
 * operator in Reviews › Integrations, so it is checked before it becomes a
 * link on a public page.
 */
const LISTING_HOSTS: Record<Exclude<ReviewPlatform, "google">, RegExp> = {
  trustpilot: /(^|\.)trustpilot\.com$/,
  tripadvisor: new RegExp(
    `(^|\\.)tripadvisor\\.(${TRIPADVISOR_ENDINGS.map(escapeDots).join("|")})$`,
  ),
  facebook: /(^|\.)facebook\.com$/,
};

const listingUrl = (
  platform: Exclude<ReviewPlatform, "google">,
  identifier: string,
): string | null => {
  try {
    const url = new URL(identifier);
    if (url.protocol !== "https:") return null;
    return LISTING_HOSTS[platform].test(url.hostname) ? url.toString() : null;
  } catch {
    return null;
  }
};

/**
 * The public page behind "See all on …". Google stores a Place ID, the
 * others a listing URL (`platform-defs.tsx` in operator-web).
 */
export const reviewPlatformUrl = (
  platform: ReviewPlatform,
  identifier: string | null,
): string | null => {
  const value = identifier?.trim();
  if (!value) return null;
  if (platform === "google") {
    return `https://search.google.com/local/reviews?placeid=${encodeURIComponent(value)}`;
  }
  return listingUrl(platform, value);
};

/** One platform's tab: its summary and its reviews, newest first. */
export interface ReviewsPlatformGroup {
  platform: ReviewPlatform;
  rating: number;
  total: number;
  url: string | null;
  reviews: ReviewCardItem[];
}

/** The bound list, grouped into tabs in tab order. */
export const groupReviewsByPlatform = (
  items: readonly ReviewCardItem[],
): ReviewsPlatformGroup[] =>
  REVIEWS_BLOCK_PLATFORMS.flatMap((platform) => {
    const reviews = items.filter((item) => item.platform === platform);
    const first = reviews[0];
    if (!first) return [];
    return [
      {
        platform,
        rating: first.platformRating,
        total: first.platformReviewTotal,
        url: first.platformUrl,
        reviews,
      },
    ];
  });

/** What a Platform reviews block is dropped with. A grid of six fills two
 *  rows at three across. */
export const PLATFORM_REVIEWS_LIMIT = 6;

/**
 * The Platform reviews grid's columns, by screen. Unset, a desktop draws 3, a
 * tablet 2 and a phone 1, which the block's CSS draws too
 * (`platform-reviews-css.ts` in `@tripcart/page-builder`).
 */
export const REVIEWS_GRID_COLUMNS = [1, 2, 3, 4] as const;
export const REVIEWS_GRID_TABLET_COLUMNS = [1, 2, 3] as const;
export const REVIEWS_GRID_MOBILE_COLUMNS = [1, 2] as const;

const readColumns =
  (options: readonly number[], fallback: number) =>
  (value: unknown): number =>
    options.find((n) => String(n) === String(value)) ?? fallback;

/**
 * How the grid sits on a phone, the Blog Posts block's switch
 * (`BLOG_POSTS_MOBILE`): `stack` is the grid, `scroll` one row that scrolls
 * sideways and snaps to each card. A drop stores `scroll`; the CSS draws the
 * grid for anything else.
 */
export const REVIEWS_GRID_MOBILE = ["stack", "scroll"] as const;
export type ReviewsGridMobile = (typeof REVIEWS_GRID_MOBILE)[number];

export const readReviewsGridTabletColumns = readColumns(
  REVIEWS_GRID_TABLET_COLUMNS,
  2,
);
export const readReviewsGridMobileColumns = readColumns(
  REVIEWS_GRID_MOBILE_COLUMNS,
  1,
);

export const REVIEWS_LAYOUTS = ["stacked", "split"] as const;
export type ReviewsLayout = (typeof REVIEWS_LAYOUTS)[number];

export const REVIEWS_CARD_STYLES = ["card", "filled", "plain"] as const;
export type ReviewsCardStyle = (typeof REVIEWS_CARD_STYLES)[number];
